import { useState, useCallback, useRef } from "react";
import { useInView } from "react-intersection-observer";

export type ThumbnailSizeKey = 292 | 480 | 1232;

function sizeFromPx(px: number): ThumbnailSizeKey {
  if (px <= 292) return 292;
  if (px <= 480) return 480;
  return 1232;
}

/**
 * Returns a ref to attach to the preview container, the inView state for lazy
 * loading, and a thumbnail size key derived from the container's actual rendered
 * dimensions rather than the item display size hint.
 *
 * The initial measurement is taken directly in the ref callback rather than
 * useLayoutEffect. React's layout phase is pre-order (parent before children),
 * so useLayoutEffect on the parent component runs before the child's ref
 * callback fires — sizeRef.current would be null. The ref callback itself fires
 * after all DOM mutations have committed, at which point getBoundingClientRect()
 * returns accurate layout dimensions.
 */
export default function useResponsiveThumbnailSize() {
  const [thumbnailSize, setThumbnailSize] = useState<ThumbnailSizeKey>(292);
  const { inView, ref: inViewRef } = useInView({ triggerOnce: true });
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      inViewRef(node);
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (node) {
        // All DOM mutations have committed by the time refs fire, so
        // getBoundingClientRect() returns correct layout dimensions here.
        const { width, height } = node.getBoundingClientRect();
        const px = Math.max(width, height);
        if (px > 0) setThumbnailSize(sizeFromPx(px));

        // Continue observing for subsequent changes (grid reflow, window resize).
        observerRef.current = new ResizeObserver(([entry]) => {
          const { width: w, height: h } = entry.contentRect;
          const p = Math.max(w, h);
          if (p > 0) setThumbnailSize(sizeFromPx(p));
        });
        observerRef.current.observe(node);
      }
    },
    [inViewRef]
  );

  return { ref, inView, thumbnailSize };
}
